import { useCallback, useEffect, useState } from 'react';
import { handleResponse } from 'common/helpers';
import { axiosInstance, ENDPOINT } from 'common/constants';

const hasWidgetPayload = (res, widgetName) => {
  if (!res || typeof res !== 'object') return false;
  const keys = Object.keys(res);
  if (!keys.length) return false;
  const hasSitesArray = payload => payload && Array.isArray(payload.sites);
  const hasOrgSummary = payload => payload && payload.org_summary != null;
  if (widgetName === 'org_fleet_health_by_site') {
    return hasSitesArray(res);
  }
  if (widgetName === 'org_sites_by_site') {
    return hasSitesArray(res) || hasOrgSummary(res);
  }
  if (widgetName === 'org_open_events_by_site') {
    return hasSitesArray(res);
  }
  if (widgetName === 'org_device_reachability_by_site') {
    return hasSitesArray(res);
  }
  if (widgetName === 'org_cost_saved_by_site') {
    return hasSitesArray(res);
  }
  if (widgetName === 'org_total_defects_by_site') {
    return hasSitesArray(res) || hasOrgSummary(res);
  }
  if (widgetName === 'org_asset_instrumentation_tasks_by_site') {
    return hasSitesArray(res) || hasOrgSummary(res);
  }
  if (widgetName === 'org_unread_unanalysed_by_site') {
    return hasSitesArray(res) || hasOrgSummary(res);
  }
  if (widgetName === 'org_device_health_rollup') {
    return Array.isArray(res.items);
  }
  if (widgetName === 'org_site_scorecard') {
    // Shape, not length — a valid empty response (e.g. all sites suspended) must
    // not be flagged as an error, or the scorecard's accounts fallback is
    // suppressed and it wrongly shows "No sites available".
    return Array.isArray(res.items) || Array.isArray(res.site_breakdown);
  }
  return keys.length > 0;
};

const resolveWidgetName = (widgetMeta) => {
  if (!widgetMeta) return null;
  if (widgetMeta.name) return widgetMeta.name;
  if (typeof widgetMeta.id === 'string' && widgetMeta.id.startsWith('org_')) {
    return widgetMeta.id.split('#')[0];
  }
  return null;
};

/**
 * Fetch org-scoped widget data by metadata id (and optional widget name fallback).
 */
export default function useOrgWidgetData(orgId, widgetMeta) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Bumped by reload() to re-run the fetch effect — backs the error-state Retry.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey(key => key + 1), []);

  useEffect(() => {
    if (!orgId || !widgetMeta) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    const params = { organisation_id: orgId };
    const widgetName = resolveWidgetName(widgetMeta);
    if (widgetName) {
      params.widget_name = widgetName;
    } else if (widgetMeta.id != null && !Number.isNaN(Number(widgetMeta.id))) {
      params.widget_id = widgetMeta.id;
    }

    handleResponse(axiosInstance.get(ENDPOINT.WIDGET_DATA, { params }))
      .then((res) => {
        if (cancelled) return;
        if (hasWidgetPayload(res, widgetName)) {
          setData(res);
        } else {
          setData(null);
          setError(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, widgetMeta && widgetMeta.id, widgetMeta && widgetMeta.name, widgetMeta && widgetMeta.title, reloadKey]);

  return { data, loading, error, reload };
}
